import * as R from 'ramda'
import { Mutex } from 'async-mutex'
import { LIMITS } from '@weblite/permission'
import throttle from 'lodash.throttle'
import {
  DeleteDraft,
  Draft,
  reqDeleteDraft,
  reqSetDraft,
  reqSyncDraft,
  SetDraft,
  SyncDraft,
} from '@weblite/ts-action/draft'
import { request } from '$setup/ws'
import { draftsStorage } from './storage'
import * as sync from './offline-sync'
import { dateWithOffset } from '$setup/time-offset'

export const getDraft = draftsStorage.getFromRedux
const mutex = new Mutex()

export async function syncDrafts(): Promise<void> {
  const { storedSyncDrafts, del, drafts } = await mutex.runExclusive(
    async () => {
      const syncObject = await sync.getAll()
      const storageDrafts = syncObject.storedSyncDrafts
        .map(({ roomId }) => getDraft(roomId))
        .filter(Boolean)
      return { ...syncObject, drafts: storageDrafts }
    },
  )

  const deletedDrafts = R.reduce(
    (acc, [roomId, updatedAt]) => ({ ...acc, [roomId]: updatedAt }),
    {},
  )(R.toPairs(storedSyncDrafts))

  const res = await request<SyncDraft>(reqSyncDraft({ drafts, deletedDrafts }))

  await draftsStorage.add(res.payload.drafts)

  await mutex.runExclusive(del)
}

export async function setImmediateDraft(
  roomId: string,
  value: Draft['value'],
): Promise<void> {
  const draft: Draft = { roomId, value, date: dateWithOffset() }
  const unsetSync = await mutex.runExclusive(async () => {
    const [unsetFunc] = await Promise.all([
      sync.set(roomId, draft.date),
      draftsStorage.add(draft),
    ])
    return unsetFunc
  })
  await request<SetDraft>(reqSetDraft(draft))
  await mutex.runExclusive(unsetSync)
}

const throttledDraft = throttle(setImmediateDraft, LIMITS.draftThrottleTime, {
  leading: false,
  trailing: true,
})

export const setDraft = throttledDraft
export const flushDraft = throttledDraft.flush.bind(throttledDraft)
export const cancelSetDraft = throttledDraft.cancel.bind(throttledDraft)

export async function deleteDraft(roomId: string): Promise<void> {
  cancelSetDraft()
  const date = dateWithOffset()
  const draft = getDraft(roomId)
  if (!draft) return
  const unsetSync = await mutex.runExclusive(async () => {
    const [unsetFunc] = await Promise.all([
      sync.set(roomId, date),
      draftsStorage.del(roomId),
    ])
    return unsetFunc
  })
  await request<DeleteDraft>(reqDeleteDraft({ roomId }))
  await unsetSync()
}
